// PDF Export Utility for Psychological Safety Pulse
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { format } from 'date-fns';
import { STAGES, RECOMMENDATIONS, INDIVIDUAL_ACTIONS, REFLECTION_PROMPTS } from '../config/constants';
import {
  calculateStageScores,
  calculateTeamAverages,
  calculateStageRange,
  identifyDevelopmentAreas,
  identifyStrengthAreas
} from './scoring';
import { getScoreInterpretation as getInterpretation } from '../config/constants';

// Color definitions for PDF
const COLORS = {
  primary: [45, 79, 77],
  secondary: [107, 158, 155],
  dark: [26, 29, 28],
  muted: [100, 110, 108],
  light: [244, 245, 244],
  white: [255, 255, 255],
  warm: [212, 133, 74],
  greenBg: [235, 248, 240],
  greenText: [22, 101, 52],
  redBg: [254, 236, 236],
  redText: [153, 27, 27],
  amberBg: [255, 243, 224],
  amberText: [180, 83, 9],
};

const PAGE_HEIGHT = 297;
const FOOTER_ZONE = 20;

// Helper: ensure enough space on page, add new page if not
const ensureSpace = (doc, yPos, needed, margin = 20) => {
  if (yPos + needed > PAGE_HEIGHT - FOOTER_ZONE) {
    doc.addPage();
    return margin;
  }
  return yPos;
};

// Helper: render a wrapped bullet list and return new yPos
const renderBulletList = (doc, items, x, yPos, maxWidth, color = COLORS.dark) => {
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...color);
  items.forEach((item) => {
    const lines = doc.splitTextToSize(`• ${item}`, maxWidth);
    const blockHeight = lines.length * 4.5;
    yPos = ensureSpace(doc, yPos, blockHeight);
    doc.text(lines, x, yPos);
    yPos += blockHeight + 2;
  });
  return yPos;
};

// Helper: section heading
const sectionHeading = (doc, text, yPos, margin = 20) => {
  yPos = ensureSpace(doc, yPos, 15);
  doc.setTextColor(...COLORS.dark);
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.text(text, margin, yPos);
  return yPos + 8;
};

// Helper: add footers to all pages
const addFooters = (doc, footerText) => {
  const totalPages = doc.internal.getNumberOfPages();
  const pageWidth = doc.internal.pageSize.getWidth();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setTextColor(160, 160, 160);
    doc.text(
      footerText,
      pageWidth / 2,
      PAGE_HEIGHT - 8,
      { align: 'center' }
    );
    doc.text(
      `Page ${i} of ${totalPages}`,
      pageWidth - 20,
      PAGE_HEIGHT - 8,
      { align: 'right' }
    );
  }
};

/**
 * Generate Team Results PDF
 */
export const generateTeamPDF = (sessionData, participants) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 20;
  const contentWidth = pageWidth - 2 * margin;
  let yPos = 20;

  const completedParticipants = participants.filter(p => p.status === 'completed');
  const teamAverages = calculateTeamAverages(participants);
  const overallScore = Object.values(teamAverages).reduce((a, b) => a + b, 0) / 4;
  const interpretation = getInterpretation(overallScore);

  // ── Header ──
  doc.setFillColor(...COLORS.primary);
  doc.rect(0, 0, pageWidth, 42, 'F');

  doc.setTextColor(...COLORS.white);
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.text('Psychological Safety Pulse', margin, 22);

  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text('Team Results Report', margin, 33);

  yPos = 52;

  // ── Session info ──
  doc.setTextColor(...COLORS.muted);
  doc.setFontSize(9);
  doc.text(`Session: ${sessionData.code}`, margin, yPos);
  doc.text(`Date: ${format(new Date(sessionData.createdAt), 'MMMM d, yyyy')}`, margin + 55, yPos);
  doc.text(`Participants: ${completedParticipants.length}`, margin + 130, yPos);

  yPos += 12;

  // ── Overall Score ──
  doc.setFillColor(...COLORS.light);
  doc.roundedRect(margin, yPos, contentWidth, 32, 3, 3, 'F');

  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.dark);
  doc.text('Overall Psychological Safety Reading', margin + 10, yPos + 13);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.muted);
  doc.text(interpretation.description, margin + 10, yPos + 22);

  doc.setFontSize(26);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...hexToRgb(interpretation.color));
  doc.text(overallScore.toFixed(1), pageWidth - margin - 10, yPos + 20, { align: 'right' });

  doc.setFontSize(9);
  doc.text(interpretation.label, pageWidth - margin - 10, yPos + 28, { align: 'right' });

  yPos += 42;

  // ── Stage Breakdown ──
  yPos = sectionHeading(doc, 'Stage Breakdown', yPos);

  const stageData = Object.entries(STAGES).map(([key, stage]) => {
    const range = calculateStageRange(participants, key);
    return [
      stage.name,
      teamAverages[key]?.toFixed(1) || '-',
      `${range.min.toFixed(1)} – ${range.max.toFixed(1)}`,
      range.spread > 1.5 ? 'High' : 'Normal'
    ];
  });

  doc.autoTable({
    startY: yPos,
    head: [['Stage', 'Average', 'Range', 'Spread']],
    body: stageData,
    theme: 'striped',
    headStyles: { fillColor: COLORS.primary, textColor: COLORS.white, fontSize: 9 },
    styles: { fontSize: 9, cellPadding: 3 },
    margin: { left: margin, right: margin }
  });

  yPos = doc.lastAutoTable.finalY + 12;

  // ── Priority Focus Area ──
  const priorityStage = Object.entries(teamAverages).sort(([,a], [,b]) => a - b)[0]?.[0];

  if (priorityStage) {
    yPos = ensureSpace(doc, yPos, 20);
    doc.setFillColor(...COLORS.amberBg);
    doc.roundedRect(margin, yPos, contentWidth, 18, 3, 3, 'F');

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.amberText);
    doc.text(`Priority Focus Area: ${STAGES[priorityStage].name}`, margin + 8, yPos + 12);

    yPos += 28;
  }

  // ── Team Recommendations: What to do ──
  yPos = sectionHeading(doc, `What to Do — ${STAGES[priorityStage]?.name || ''}`, yPos);

  const dos = RECOMMENDATIONS[priorityStage]?.dos || [];
  yPos = renderBulletList(doc, dos, margin, yPos, contentWidth, COLORS.greenText);

  yPos += 4;

  // ── Team Recommendations: What to avoid ──
  yPos = sectionHeading(doc, `What to Avoid — ${STAGES[priorityStage]?.name || ''}`, yPos);

  const donts = RECOMMENDATIONS[priorityStage]?.donts || [];
  yPos = renderBulletList(doc, donts, margin, yPos, contentWidth, COLORS.redText);

  yPos += 8;

  // ── Participant Readings ──
  yPos = ensureSpace(doc, yPos, 40);
  yPos = sectionHeading(doc, 'Participant Readings', yPos);

  const participantData = completedParticipants.map(p => {
    const scores = calculateStageScores(p.responses);
    const overall = Object.values(scores).reduce((a, b) => a + b, 0) / 4;
    return [
      p.name,
      scores.inclusion?.toFixed(1) || '-',
      scores.learner?.toFixed(1) || '-',
      scores.contributor?.toFixed(1) || '-',
      scores.challenger?.toFixed(1) || '-',
      overall.toFixed(1)
    ];
  });

  doc.autoTable({
    startY: yPos,
    head: [['Name', 'Inclusion', 'Learner', 'Contributor', 'Challenger', 'Overall']],
    body: participantData,
    theme: 'striped',
    headStyles: { fillColor: COLORS.primary, textColor: COLORS.white, fontSize: 8 },
    styles: { fontSize: 8, cellPadding: 3 },
    margin: { left: margin, right: margin }
  });

  // ── Footers ──
  addFooters(doc, `Psychological Safety Pulse — Alex Popa-Antohi | Generated ${format(new Date(), 'MMM d, yyyy')}`);

  return doc;
};

/**
 * Generate Individual Results PDF
 */
export const generateIndividualPDF = (participant, sessionData, participants) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 20;
  const contentWidth = pageWidth - 2 * margin;
  let yPos = 20;

  const teamAverages = calculateTeamAverages(participants);
  const participantScores = calculateStageScores(participant.responses);
  const overallScore = Object.values(participantScores).reduce((a, b) => a + b, 0) / 4;
  const teamOverall = Object.values(teamAverages).reduce((a, b) => a + b, 0) / 4;
  const interpretation = getInterpretation(overallScore);
  const strengths = identifyStrengthAreas(participant.responses, teamAverages);
  const development = identifyDevelopmentAreas(participant.responses, teamAverages);

  // ── Header ──
  doc.setFillColor(...COLORS.primary);
  doc.rect(0, 0, pageWidth, 42, 'F');

  doc.setTextColor(...COLORS.white);
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.text(`${participant.name}'s Insights`, margin, 22);

  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text('Personal Psychological Safety Pulse', margin, 33);

  yPos = 52;

  // ── Session info ──
  doc.setTextColor(...COLORS.muted);
  doc.setFontSize(9);
  doc.text(`Session: ${sessionData.code}`, margin, yPos);
  doc.text(`Date: ${format(new Date(participant.completedAt || sessionData.createdAt), 'MMMM d, yyyy')}`, margin + 55, yPos);

  yPos += 12;

  // ── Score boxes side by side ──
  const halfWidth = (contentWidth - 8) / 2;

  // Your Reading
  doc.setFillColor(...COLORS.light);
  doc.roundedRect(margin, yPos, halfWidth, 28, 3, 3, 'F');
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.muted);
  doc.text('Your Reading', margin + 8, yPos + 10);
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...hexToRgb(interpretation.color));
  doc.text(overallScore.toFixed(1), margin + 8, yPos + 23);

  // Team Average
  const rightX = margin + halfWidth + 8;
  doc.setFillColor(...COLORS.light);
  doc.roundedRect(rightX, yPos, halfWidth, 28, 3, 3, 'F');
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.muted);
  doc.text('Team Average', rightX + 8, yPos + 10);
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.dark);
  doc.text(teamOverall.toFixed(1), rightX + 8, yPos + 23);

  yPos += 38;

  // ── Stage Comparison ──
  yPos = sectionHeading(doc, 'Your Readings vs Team', yPos);

  const stageComparisonData = Object.entries(STAGES).map(([key, stage]) => {
    const myScore = participantScores[key];
    const teamScore = teamAverages[key];
    const diff = myScore - teamScore;
    return [
      stage.name,
      myScore?.toFixed(1) || '-',
      teamScore?.toFixed(1) || '-',
      `${diff >= 0 ? '+' : ''}${diff.toFixed(1)}`
    ];
  });

  doc.autoTable({
    startY: yPos,
    head: [['Stage', 'Your Reading', 'Team Avg', 'Difference']],
    body: stageComparisonData,
    theme: 'striped',
    headStyles: { fillColor: COLORS.primary, textColor: COLORS.white, fontSize: 9 },
    styles: { fontSize: 9, cellPadding: 3 },
    margin: { left: margin, right: margin }
  });

  yPos = doc.lastAutoTable.finalY + 12;

  // ── Strengths ──
  if (strengths.length > 0) {
    yPos = sectionHeading(doc, 'Your Strengths', yPos);

    strengths.forEach((stage) => {
      yPos = ensureSpace(doc, yPos, 30);

      // Stage name
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...COLORS.secondary);
      doc.text(STAGES[stage].name, margin, yPos);
      yPos += 6;

      // Why this is your strength
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...COLORS.muted);
      doc.text('WHY THIS IS YOUR STRENGTH', margin, yPos);
      yPos += 5;

      const leveraging = INDIVIDUAL_ACTIONS[stage]?.leveraging?.slice(0, 3) || [];
      yPos = renderBulletList(doc, leveraging, margin, yPos, contentWidth, COLORS.dark);

      yPos += 1;

      // Actions to help your team
      yPos = ensureSpace(doc, yPos, 15);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...COLORS.muted);
      doc.text('ACTIONS TO HELP YOUR TEAM', margin, yPos);
      yPos += 5;

      const high = INDIVIDUAL_ACTIONS[stage]?.high?.slice(0, 3) || [];
      yPos = renderBulletList(doc, high, margin, yPos, contentWidth, COLORS.dark);

      yPos += 4;
    });
  }

  // ── Growth Opportunities ──
  if (development.length > 0) {
    yPos = sectionHeading(doc, 'Growth Opportunities', yPos);

    development.slice(0, 2).forEach((stage) => {
      yPos = ensureSpace(doc, yPos, 25);

      // Stage name
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...COLORS.warm);
      doc.text(STAGES[stage].name, margin, yPos);
      yPos += 6;

      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...COLORS.muted);
      doc.text('ACTIONS TO TRY', margin, yPos);
      yPos += 5;

      const low = INDIVIDUAL_ACTIONS[stage]?.low?.slice(0, 3) || [];
      yPos = renderBulletList(doc, low, margin, yPos, contentWidth, COLORS.dark);

      yPos += 4;
    });
  }

  // ── Reflection Prompts ──
  yPos = sectionHeading(doc, 'Reflection Prompts', yPos);

  const focusStage = development[0] || strengths[0] || 'inclusion';

  Object.entries(REFLECTION_PROMPTS).forEach(([key, source]) => {
    yPos = ensureSpace(doc, yPos, 22);

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...hexToRgb(source.color));
    doc.text(source.title, margin, yPos);
    yPos += 5;

    const prompts = source.prompts[focusStage]?.slice(0, 3) || [];
    yPos = renderBulletList(doc, prompts, margin, yPos, contentWidth, COLORS.dark);

    yPos += 2;
  });

  // ── Footers ──
  addFooters(doc, `Psychological Safety Pulse — Alex Popa-Antohi | Report for ${participant.name} | ${format(new Date(), 'MMM d, yyyy')}`);

  return doc;
};

/**
 * Download PDF helper
 */
export const downloadPDF = (doc, filename) => {
  doc.save(`${filename}.pdf`);
};

/**
 * Convert hex color to RGB array
 */
const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)]
    : [0, 0, 0];
};
